import { FC, useCallback } from 'react';

import { TabChangeDetail } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useBrowserQueryState } from '@telialt/common-hooks/src/useBrowserQueryState';
import { useDynamicQueryParamKey } from '@telialt/common-hooks/src/useDynamicQueryParamKey';
import { ProductsSelectedFilters } from '@telialt/core-search/src/Common/Facets/ProductsSelectedFilters';

import { SORTING_QUERY_PARAM, TELIA1_QUERY_PARAM } from '../../../../constants';
import { Commitment, OfferGroup } from '../../../../model';
import { PLANS_TEST_IDS } from '../../../../test-ids';
import { getAvailableCommitments, getSortSelectOptions } from '../../../../utils';
import { FacetFilteringControls } from '../Facets/model';
import { PlansFacetsDrawer } from '../Facets/PlansFacetsDrawer';
import {
    FiltersRow,
    StyledPlansBlockHeader,
    StyledSelect,
    StyledTabs,
    TabsContainer,
    ToggleTelia1,
} from './PlansBlockHeader.styles';

type Props = {
    possibleCommitmentTypes: Commitment[];
    hasSorting: boolean;
    hasFilters: boolean;
    hasTelia1Toggle?: boolean;
    initialSortLabel?: string;
    commitmentKey?: string;
    defaultCommitmentKey?: string;
    teliaOneKey?: string;
    listId?: string;
    offerGroup: OfferGroup;
};

export const PlansBlockHeader: FC<Props & FacetFilteringControls> = ({
    possibleCommitmentTypes,
    hasSorting,
    hasFilters,
    hasTelia1Toggle,
    initialSortLabel,
    commitmentKey,
    defaultCommitmentKey,
    teliaOneKey,
    listId,
    facets,
    filters,
    appliedFilters,
    offerGroup,
    onFilterChange,
    onRemoveAllFilters,
    onRemoveFilter,
}) => {
    const { t } = useTranslations();

    const defaultSortLabel = getSortSelectOptions(t)[0].label;
    const [sortLabel, setSortLabel] = useBrowserQueryState<string>(
        SORTING_QUERY_PARAM,
        defaultSortLabel,
        initialSortLabel,
    );
    const sortOptions = getSortSelectOptions(t).map((option) => ({
        label: option.label,
        value: option.label,
    }));

    const availableCommitments = getAvailableCommitments(t, possibleCommitmentTypes, offerGroup);
    const commitmentKeys = availableCommitments.map((option) => option?.paramKey);
    const [, setCommitmentKey] = useDynamicQueryParamKey({
        possibleKeys: commitmentKeys,
        defaultKey: defaultCommitmentKey,
    });

    const [, setTeliaOneKey] = useDynamicQueryParamKey({
        possibleKeys: [TELIA1_QUERY_PARAM],
    });

    const handleCommitmentChange = useCallback(
        (event: CustomEvent<TabChangeDetail>) => {
            const newCommitmentKey = commitmentKeys.find((option) => option === event.detail.value);

            setCommitmentKey(newCommitmentKey);
        },
        [commitmentKeys, setCommitmentKey],
    );

    return (
        <StyledPlansBlockHeader>
            {!!availableCommitments.length && (
                <TabsContainer>
                    <StyledTabs
                        aria-label={t['plans.plans_list.header_filters.label']}
                        variant="line"
                        onChange={handleCommitmentChange}
                        defaultValue={defaultCommitmentKey}
                        value={commitmentKey || defaultCommitmentKey}
                        aria-controls={listId}
                    >
                        {availableCommitments.map((commitment) => (
                            <StyledTabs.Content
                                key={commitment.paramKey}
                                name={commitment.label}
                                tabId={commitment.paramKey}
                            >
                                <div />
                            </StyledTabs.Content>
                        ))}
                    </StyledTabs>
                </TabsContainer>
            )}
            {(hasFilters || hasSorting) && (
                <FiltersRow>
                    {hasTelia1Toggle && hasFilters && (
                        <ToggleTelia1
                            aria-label={`${t['plans.plans_list.header_filters.label']}: ${t['plans.plans_list.filters.telia1_label']}`}
                            id={PLANS_TEST_IDS.TELIA1_TOGGLE}
                            checked={!!teliaOneKey}
                            onChange={(checked) => {
                                setTeliaOneKey(checked ? TELIA1_QUERY_PARAM : undefined);
                            }}
                            label={t['plans.plans_list.filters.telia1_label']}
                            aria-controls={listId}
                        />
                    )}

                    {hasFilters && (
                        <PlansFacetsDrawer
                            facets={facets}
                            filters={filters}
                            onFilterChange={onFilterChange}
                            onRemoveAllFilters={onRemoveAllFilters}
                        />
                    )}

                    {hasSorting && (
                        <StyledSelect
                            aria-label={t['plans.plans_list.sorting.label']}
                            value={sortLabel}
                            onChange={(event) => setSortLabel(event.target.value)}
                            options={sortOptions}
                            aria-controls={listId}
                        />
                    )}
                </FiltersRow>
            )}
            {hasFilters && (
                <ProductsSelectedFilters
                    facets={facets}
                    filters={appliedFilters}
                    onRemoveFilter={onRemoveFilter}
                    onRemoveAllFilters={onRemoveAllFilters}
                />
            )}
        </StyledPlansBlockHeader>
    );
};
